<template>
  <div class="product-gallery">
    <!-- media -->
    <swiper
      v-if="mounted"
      ref="images"
      :instance-name="[`${liquid.sectionId}-images`]"
      :navigation="navigation"
      :modules="modules"
      :zoom="true"
      :breakpoints="breakpoints"
      :pagination="{
        type: 'fraction',
      }"
      :thumbs="{
        swiper: thumbsSwiper
      }"
      class="product-gallery__images-wrapper"
      @slide-change="getCurrentIndex(); pauseAllVideos();"
      @swiper="setImagesSwiper"
    >
      <template v-if="product">
        <template
          v-for="(media, index) in productMedia"
          :key="`media-${index}-${media.id}`"
        >
          <swiper-slide
            v-if="media.media_type == 'image'"
            :key="`media-${index}-${media.id}`"
            class="product-gallery__image-container"
          >
            <div
              class="swiper-zoom-container"
            >
              <responsive-image
                v-if="media"
                class="product-gallery__image swiper-zoom-target"
                :alt="media.alt"
                :image="media"
                :lazy-load="true"
                min-max="600-1200"
                ratio="1:1"
              />
            </div>
          </swiper-slide>
          <swiper-slide
            v-if="media.media_type == 'video'"
            class="product-gallery__video-slide"
          >
            <div class="product-gallery__video-container">
              <video-element
                :srcset="media.sources"
                :controls="true"
                :loop="false"
                :autoplay="false"
                :title="media.alt"
                :poster="media.preview_image.src"
                :muted="false"
              />
            </div>
          </swiper-slide>
        </template>
      </template>

      <div
        v-else
        class="
          product-gallery__image-container
          product-gallery__image-container--loading
        "
      />

      <div
        v-if="badges"
        class="product-gallery__promo-badges"
      >
        <div
          v-for="(badge, index) in badges"
          :key="`badge-${index}`"
        >
          <img
            v-if="badge.english"
            class="product-gallery__badge"
            :class="{
              'show-en': badge.arabic
            }"
            :src="badge.english.src"
            :alt="badge.english.alt"
          >

          <img
            v-if="badge.arabic"
            class="product-gallery__badge show-ar"
            :src="badge.arabic.src"
            :alt="badge.arabic.alt"
          >
        </div>
      </div>
      <button
        class="product-gallery__fullscreen-button"
        @click="showFullscreen(), fullscreenProductGalleryDataLayer(product)"
      >
        <span
          class="visually-hidden"
          v-text="`${liquid.openFullscreenLabel}`"
        />
        <icon-fullscreen />
      </button>
    </swiper>

    <button
      :class="[`swiper-button-prev-${liquid.sectionId}`]"
      class="collection-icons__swiper-arrow swiper-arrow swiper-button-prev"
    >
      <span
        class="visually-hidden"
        v-text="`${liquid.previousSlideLabel}`"
      />
      <icon-chevron-left />
    </button>

    <button
      :class="[`swiper-button-next-${liquid.sectionId}`]"
      class="collection-icons__swiper-arrow swiper-arrow swiper-button-next"
    >
      <span
        class="visually-hidden"
        v-text="`${liquid.nextSlideLabel}`"
      />
      <icon-chevron-right />
    </button>

    <!-- thumbnails -->
    <swiper
      :instance-name="[`${liquid.sectionId}-thumbnails`]"
      :space-between="8"
      :modules="modules"
      :slides-per-view="'auto'"
      :slide-to-clicked-slide="true"
      class="product-gallery__thumbnails-wrapper"
      watch-slide-progress
      @slide-change="getCurrentIndex"
      @swiper="setThumbsSwiper"
    >
      <template
        v-for="(media, index) in productMedia"
        :key="`thumbnail-${index}-${media.id}`"
      >
        <swiper-slide
          v-if="media.media_type == 'image'"
          :key="`thumbnail-${index}-${media.id}`"
          class="product-gallery__thumbnail-container"
        >
          <responsive-image
            class="product-gallery__thumbnail"
            :alt="media.alt"
            :image="media"
            :lazy-load="true"
            ratio="1:1"
          />
        </swiper-slide>
        <swiper-slide
          v-if="media.media_type == 'video'"
          class="product-gallery__thumbnail-container"
        >
          <responsive-image
            class="product-gallery__thumbnail"
            :alt="media.alt"
            :image="media.preview_image"
            :lazy-load="true"
            ratio="1:1"
          />
          <icon-video-start />
        </swiper-slide>
      </template>
    </swiper>
  </div>
  <overlay
    namespace="fullscreenProductGallery"
    :template="false"
    :full-modal="true"
    type="modal"
    direction="up"
  >
    <template #body="{isActive}">
      <fullscreen-product-gallery
        v-if="getComponentVisible('fullscreen-product-gallery') && isActive"
        :liquid="liquid"
        :product="product"
        :variant="variant"
        :current-index="currentIndex"
        @close="hideFullscreen"
        @updated-index="handleFullscreenSwiperChange"
      />
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Product images gallery.
 *
 */
import { ref, nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'

import { A11y, Pagination, Navigation, Thumbs, Zoom } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'

import { values } from '~/config/breakpoints'

import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'
import IconFullscreen from '~icons/misc/fullscreen.svg'
import IconVideoStart from '~icons/video/start.svg'
import FullscreenProductGallery from '~async/fullscreen-product-gallery/fullscreen-product-gallery'
import ResponsiveImage from '~global/images/responsive-image.vue'
import VideoElement from '~async/video-element/video-element'

export default {
  name: 'ProductGallery',

  components: {
    Swiper,
    SwiperSlide,
    IconChevronLeft,
    IconChevronRight,
    IconFullscreen,
    IconVideoStart,
    FullscreenProductGallery,
    ResponsiveImage,
    VideoElement,
},

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Object, Boolean],
      default: false,
    },
  },

  setup(props) {

    const imagesSwiper = ref(null)
    const thumbsSwiper = ref(null)
    const breakpoints = {}

    const setImagesSwiper = (swiper) => {
      imagesSwiper.value = swiper
    }

    const setThumbsSwiper = (swiper) => {
      thumbsSwiper.value = swiper
    }

    breakpoints[values.l] = {
      zoom: false,
    }

    return {
      imagesSwiper,
      thumbsSwiper,
      setImagesSwiper,
      setThumbsSwiper,
      breakpoints,
      modules: [
        A11y,
        Pagination,
        Navigation,
        Zoom,
        Thumbs,
      ],
      navigation: {
        nextEl: `.swiper-button-next-${props.liquid.sectionId}`,
        prevEl: `.swiper-button-prev-${props.liquid.sectionId}`,
      },
    }
  },

  data() {
    return {
      currentIndex: 0,
      mounted: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if any of the product tags match the product badge tags setup
     * in its section.
     */
    badges() {
      if (!this.product || !this.product.tags.length || !this.$variable('badges').length) {
        return
      }

      const badges = []

      this.$variable('badges').forEach((block) => {
        if (this.product.tags.includes(block.tag) && badges.length < 4) {
          const badge = {
            english: block.badgeEn,
            arabic: block.badgeAr,
          }

          badges.push(badge)
        }
      })

      return badges
    },

    /**
     * Compute the product images.
     * @returns {Object}
     */
    productMedia() {
      if (!this.product || !this.variant) {
        return
      }

      const media = [...this.liquid.media]

      const filteredImages = media.filter((media) => {
        return media.src?.includes(this.variant.sku)
      })

      return filteredImages
    },

  },

  watch: {

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      if (this.isMobile) {
        this.closeOverlay('fullscreenProductGallery')
      }
    },

    variant() {
      if (!this.variant) {
        return
      }

      this.handleVariantImageUpdate()
    },

    /**
     * Watch for change in product images array and re-update Swiper
     */
    productMedia() {
      nextTick(() => {
        if (this.imagesSwiper) {
          this.imagesSwiper.update()
        }

        if (this.thumbsSwiper) {
          this.thumbsSwiper.update()
        }
      })
    },
  },

  mounted() {
    nextTick(() => {
      this.mounted = true
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      closeOverlay: 'overlays/close',
    }),

    /**
     * Get current index of Swiper slides.
     */
    getCurrentIndex() {
      const index = this.imagesSwiper.activeIndex
      this.setCurrentIndex(index)
    },

    /**
     * Pause all videos in the document.
     */
    pauseAllVideos() {
      const video = document.querySelectorAll('video')

      if (!video) {
        return
      }

      video.forEach((element) => {
        element.pause()
      })
    },

    /**
     * Set current index of Swiper slides.
     */
    setCurrentIndex(index) {
      this.currentIndex = index
    },

    handleFullscreenSwiperChange(index) {
      this.setCurrentIndex(index)
      this.imagesSwiper.slideTo(index)
    },

    /**
     * Events for when the open fullscreen button is clicked.
     */
    showFullscreen() {
      this.openOverlay({
        component: 'fullscreen-product-gallery',
        ignoreDismissed: true,
        namespace: 'fullscreenProductGallery',
      })
    },

    /**
     * Events for when the close fullscreen button is clicked.
     */
    hideFullscreen() {
      this.closeOverlay('fullscreenProductGallery')
    },

    /**
     * Update the product carousel when the variant changes.
     * - Looks through the `productMedia` array to find the first image with
     *   a `variant` property that matches the current `variant.id`.
     * - Slides to that image if one is found.
     */
    handleVariantImageUpdate() {
      const variantImageIndex = this.productMedia.findIndex(((imageObject) => {
        return this.variant.id === imageObject.variant
      }))

      if (variantImageIndex === -1) {
        return
      }

      this.imagesSwiper.slideTo(variantImageIndex)
    },

    /**
     * Send the fullscreen gallery click event info to the data layer.
     * @param {Object} product - the product object.
     */
    fullscreenProductGalleryDataLayer(product) {
      cnvs.EventBus.emit('Datalayer:fullscreenProductGallery', product)
    },

  },
}
</script>

<style lang="scss">
@import './product-gallery';
</style>
